import { useEffect, useState } from 'react'
import { Eye, History } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { toast } from 'sonner'
import FormRenderer from '../../components/FormRenderer'
import { platformApi } from '../../api'
import type { FormVersion } from '../../types'
import { describeShort, normalizeDefinition } from './model'

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '')
const statusVariant = { published: 'default', draft: 'secondary', superseded: 'outline' } as const

export default function VersionsTab({ formId, refreshKey }: { formId: string; refreshKey: number }) {
    const [versions, setVersions] = useState<FormVersion[] | null>(null)
    const [viewing, setViewing] = useState<FormVersion | null>(null)

    useEffect(() => {
        let live = true
        setVersions(null)
        platformApi.listVersions(formId)
            .then((v) => { if (live) setVersions(v) })
            .catch((e: Error) => { toast.error(e.message); if (live) setVersions([]) })
        return () => { live = false }
    }, [formId, refreshKey])

    if (!versions) return <div className="flex flex-col gap-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
    if (versions.length === 0) {
        return (
            <Empty className="border border-dashed">
                <EmptyHeader>
                    <EmptyMedia variant="icon"><History /></EmptyMedia>
                    <EmptyTitle>No versions yet</EmptyTitle>
                    <EmptyDescription>Publish the draft to create the first version.</EmptyDescription>
                </EmptyHeader>
            </Empty>
        )
    }
    const def = viewing ? normalizeDefinition(viewing.definition) : null
    return (
        <>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Version</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Changelog</TableHead>
                        <TableHead>Checksum</TableHead>
                        <TableHead>Published</TableHead>
                        <TableHead><span className="sr-only">Actions</span></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {versions.map((v) => (
                        <TableRow key={v.version}>
                            <TableCell className="font-medium">{v.version}</TableCell>
                            <TableCell><Badge variant={statusVariant[v.status] ?? 'outline'} className="capitalize">{v.status}</Badge></TableCell>
                            <TableCell className="max-w-sm whitespace-normal text-muted-foreground">{v.changelog || 'No changelog'}</TableCell>
                            <TableCell><code className="font-mono text-xs" title={v.checksum}>{v.checksum ? v.checksum.slice(0, 8) : ''}</code></TableCell>
                            <TableCell className="text-muted-foreground">{fmt(v.published_at)}</TableCell>
                            <TableCell className="text-right">
                                <Button variant="outline" size="sm" onClick={() => setViewing(v)}><Eye data-icon="inline-start" /> View</Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Sheet open={viewing !== null} onOpenChange={(o) => { if (!o) setViewing(null) }}>
                <SheetContent className="w-full overflow-y-auto data-[side=right]:sm:max-w-2xl">
                    <SheetHeader>
                        <SheetTitle>Version {viewing?.version}</SheetTitle>
                        <SheetDescription>{def ? `Read-only. ${describeShort(def)}.` : ''}</SheetDescription>
                    </SheetHeader>
                    <div className="flex flex-col gap-8 px-4 pb-6">
                        {def?.pages.map((p) => (
                            <section key={p.id} className="flex flex-col gap-3" aria-label={p.title}>
                                <h3 className="text-sm font-semibold">{p.title}</h3>
                                <fieldset disabled className="min-w-0 border-0 p-0">
                                    <FormRenderer page={p} answers={{}} onChange={() => undefined} />
                                </fieldset>
                            </section>
                        ))}
                    </div>
                </SheetContent>
            </Sheet>
        </>
    )
}
